
export type DayOfWeek = 'Monday' | 'Tuesday' | 'Wednesday' | 'Thursday' | 'Friday' | 'Saturday' | 'Sunday';
export const ALL_DAYS: DayOfWeek[] = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
export const DAY_INDEX_MAP: { [key in DayOfWeek]: number } = {
    'Sunday': 0,
    'Monday': 1,
    'Tuesday': 2,
    'Wednesday': 3,
    'Thursday': 4,
    'Friday': 5,
    'Saturday': 6
};

export type AvailabilityStatus = 'Available' | 'Preferred' | 'Unavailable';
export type AssignmentStatus = 'ASSIGNED' | 'OFF' | 'SICK' | 'VACATION' | 'ON_CALL' | 'REFUSED' | 'UNASSIGNED';
export const ALL_ASSIGNMENT_STATUSES: AssignmentStatus[] = ['ASSIGNED', 'OFF', 'SICK', 'VACATION', 'ON_CALL', 'REFUSED', 'UNASSIGNED'];
export type TimeOffRequestType = 'DIRECT' | 'REPLACED';
export type TimeOffRequestStatus = 'PENDING_ADMIN' | 'PENDING_REPLACER' | 'APPROVED' | 'REJECTED';
export type Theme = 'light' | 'dark' | 'system';

export type AttendanceRequestStatus = 'PENDING' | 'APPROVED' | 'REJECTED';

export interface AttendanceRequest {
    id: string;
    employeeId: string;
    date: string; // YYYY-MM-DD
    shiftId: string;
    originalStartTime: string;
    originalEndTime: string;
    requestedStartTime: string;
    requestedEndTime: string;
    staffNote?: string;
    adminNote?: string;
    status: AttendanceRequestStatus;
    autoApproved?: boolean;
    createdAt: string; // ISO string
    reviewedAt?: string; // ISO string
    reviewedBy?: string; // Admin UID or Name
}

export interface AttendanceArchive {
    id: string;
    employeeId: string;
    date: string;
    originalTimeIn: string;
    originalTimeOut: string;
    requestedTimeIn: string;
    requestedTimeOut: string;
    finalStatus: AttendanceRequestStatus;
    approvedAt?: string;
    archivedAt: string;
}

export interface PayrollPeriod {
    id: string;
    startDate: string;
    endDate: string;
    isClosed: boolean;
    closedAt?: string;
    closedBy?: string;
}

export interface Department {
    id: string;
    name: string;
    abbreviation: string;
    color: string;
    order?: number;
}

export interface StaffMember {
    id: string;
    name: string;
    color: string;
    email: string;
    phone: string;
    role: string;
    notes: string;
    type?: 'staff' | 'admin';
    departmentIds: string[]; // First one is primary
    isDriver?: boolean;
    password?: string;
    status?: 'active' | 'deleted' | 'suspended';
    availability: {
        [key in DayOfWeek]: AvailabilityStatus;
    };
    availabilityOverrides?: {
        [date: string]: AvailabilityStatus; // Key is 'YYYY-MM-DD'
    };
    createdAt?: string;
    updatedAt?: string;
    notification?: Partial<Notification> | null;
}

export interface Shift {
    id: string;
    name:string;
    startTime: string; // "HH:mm" format
    endTime: string;   // "HH:mm" format
    color: string;
}

export interface PickupLocation {
    id: string;
    name: string;
    address: string;
    latitude: number;
    longitude: number;
    color: string;
}

export interface ReservationSource {
    id: string;
    name: string;
}

export interface ShuttleReservation {
    id: string;
    locationId: string;
    direction: 'TO_HOTEL' | 'FROM_HOTEL';
    time: string;
    date: string; // YYYY-MM-DD
    guests: number;
    roomNumber: string;
    createdAt: string;
    status?: 'pending' | 'done' | 'blocked';
    spotIndex?: number;
}

export interface ScheduleEntry {
    staffId: string;
    shiftId: string | null; // Keep for legacy/UI but logic uses assignedTimes
    status: AssignmentStatus;
    departmentId?: string | null; // Specific department for this shift
    isForced?: boolean;
    assignedStartTime?: string; // Decoupled time in
    assignedEndTime?: string;   // Decoupled time out
    shiftNameSnapshot?: string;  // Template name at assignment
    shiftColorSnapshot?: string; // Template color at assignment
    customStartTime?: string;
    customEndTime?: string;
    isTimeEdited?: boolean;
    isApproved?: boolean;
    isRejected?: boolean;
}

export interface AttendanceRecord {
    id: string; // recordId
    employeeId: string;
    shiftId: string;
    date: string; // YYYY-MM-DD
    scheduledStartTime: string;
    scheduledEndTime: string;
    actualStartTime: string;
    actualEndTime: string;
    totalHours: number;
    extraHours: number;
    status: 'AUTO_APPROVED' | 'PENDING' | 'APPROVED' | 'REJECTED';
    approvedAt?: string;
    approvedBy?: string;
    staffNote?: string;
    adminNote?: string;
}

export interface DailyMetrics {
    arrivals: number;
    departures: number;
    stayOvers: number;
}

export interface WeeklyMetrics {
    [date: string]: DailyMetrics; // Key is 'YYYY-MM-DD'
}

export interface WorkloadFactors {
    arrivalTime: number; // AT
    departureTime: number; // DT
    stayoverTime: number; // ST
    bufferMultiplier: number; // BF
    workloadFactor: number; // New: WF
}

export interface Schedule {
    [staffId: string]: {
        [date: string]: ScheduleEntry; // Key is 'YYYY-MM-DD'
    };
}

export interface ParkingReservation {
    id: string;
    reservationSource: string;
    confirmationNumber: string;
    numberOfPeople: string;
    authorizedBy: string;
    vehicle_plate: string;
    guestName: string;
    roomNumber: string;
    startDate: string; // YYYY-MM-DD (From)
    endDate: string; // YYYY-MM-DD (To)
    hotelCheckInDate?: string; // New
    hotelCheckOutDate?: string; // New
    roomType?: string; // New
    datesOfSleep?: string; // New
    numberOfParkings?: string; // New
    numberOfRooms?: string; // New
    status: 'Reservation' | 'Check-in' | 'Checkout' | 'No Show' | 'Disactivated' | 'Late Departure' | 'OVERDUE' | 'Due Checkout' | 'Late Checkout';
    eta?: string;
    customerPhone?: string; // New
    customerEmail?: string; // New
    total?: string; // New
    carMakeModel?: string; // New
    dueAtLocation?: string; // New
    numberOfDays?: string; // New
    notes?: string;
    createdAt: string; // Creation Date
    updatedAt?: string; // Last Update Date
    notifiedStatuses?: string[]; // Track which statuses have been notified
    archived?: boolean;
}

export interface Payment {
    id: string;
    reservationId: string;
    amount: number;
    currency: string;
    status: 'pending' | 'completed' | 'failed' | 'refunded';
    method: 'credit_card' | 'cash' | 'other';
    transactionId?: string;
    createdAt: string;
    updatedAt: string;
}

export interface Role {
    id: string;
    name: string;
    permissions: string[];
    createdAt: string;
    updatedAt: string;
}

export interface Vehicle {
    id: string;
    plate: string;
    makeModel?: string;
    userId?: string; // Owner
    status: 'active' | 'inactive';
    createdAt: string;
    updatedAt: string;
}

export interface ClosedPeriod {
    id: string; // YYYY-MM-DD_YYYY-MM-DD
    startDate: string;
    endDate: string;
    closedAt: string;
    closedBy: string;
}

export interface AccessRecord {
    id: string;
    websiteName: string;
    url: string;
    username: string;
    password: string;
    notes?: string;
    createdAt: string;
    updatedAt: string;
}

export type AppPage = 'scheduler' | 'schedulerPreview' | 'staffList' | 'settings' | 'myProfile' | 'timeOff' | 'workforce' | 'shuttle' | 'parking' | 'reports' | 'help' | 'attendance' | 'taskManager' | 'memos' | 'maintenance' | 'accessManager';
export const ALL_PAGES: AppPage[] = ['scheduler', 'schedulerPreview', 'staffList', 'timeOff', 'shuttle', 'parking', 'workforce', 'reports', 'attendance', 'taskManager', 'memos', 'maintenance', 'accessManager', 'settings', 'myProfile', 'help'];

export type ReportPermissionLevel = 'none' | 'view' | 'generate' | 'export';

export interface ReportPermission {
    reportId: string;
    level: ReportPermissionLevel;
}

export const REPORT_CONFIG = [
    { id: 'workingHours', label: 'Staff Working Hours' },
    { id: 'nightAuditSummary', label: 'Daily Night Audit Report Summary' },
    { id: 'dailyOperation', label: 'Daily Operation Report' },
    { id: 'parking', label: 'Parking Report' },
    { id: 'maintenance', label: 'Maintenance Report' },
    { id: 'attendance', label: 'Attendance Report' },
    { id: 'payrollSummary', label: 'Payroll Summary Report' },
] as const;

export type ReportId = typeof REPORT_CONFIG[number]['id'];

export interface View {
    page: AppPage;
    staffId?: string;
}

export interface LoginCredentials {
    email?: string;
    phone?: string;
    password: string;
    name?: string;
}

export interface AdminUser {
    uid: string;
    email: string;
    status: 'active' | 'suspended' | 'deleted';
    type?: 'staff' | 'admin';
    // Profile fields
    name: string;
    color: string;
    phone: string;
    role: string;
    notes: string;
    departmentIds: string[]; // First one is primary
    isDriver?: boolean;
    availability: {
        [key in DayOfWeek]: AvailabilityStatus;
    };
    availabilityOverrides?: {
        [date: string]: AvailabilityStatus; // Key is 'YYYY-MM-DD'
    };
    showInScheduler: boolean;
    allowedPages?: AppPage[];
    reportPermissions?: { [reportId: string]: ReportPermissionLevel };
    createdAt?: string;
    updatedAt?: string;
    notification?: Partial<Notification> | null;
    settings?: {
        general?: {
            showLaborFactors?: boolean;
        };
    };
}

export interface Notification {
  id: string;
  userId: string;
  recordId: string;
  eventType: string;
  title: string;
  message: string;
  level: "info" | "warning" | "error" | "success";
  scheduledAt: string; // ISO string ONLY
  read: boolean;
}

export interface NightAuditReport {
    id: string;
    date: string; // YYYY-MM-DD
    startTime?: string; // HH:mm
    endTime?: string;   // HH:mm
    status?: 'in-progress' | 'completed';
    completedAt: string; // ISO string
    completedBy: string; // Staff name or ID
    currentStepIndex?: number;
    totalTasks: number;
    completedTasks: number;
    completionPercentage: number;
    steps: {
        id: number;
        title: string;
        completedCount: number;
        totalCount: number;
        items: {
            label: string;
            isDone: boolean;
            note?: string;
            subs?: string[];
        }[];
    }[];
    fastReportData?: {
        date: string;
        inHouseRooms: string;
        compRooms: string;
        ullivikNoShowAssigned: { count: string; details: string };
        ullivikNoShowKeep: { count: string; details: string };
        regularNoShow: { count: string; details: string };
        changeRoomsFrom: string;
        changeRoomsTo: string;
    };
}

export interface DailyNightAuditReport {
    id: string;
    date: string; // YYYY-MM-DD (Yesterday)
    displayDate: string;
    inHouseRooms: number;
    compRooms: number;
    ullivikNoShowAssigned: { count: number; details: string };
    ullivikNoShowKeep: { count: number; details: string };
    regularNoShow: { count: number; details: string };
    roomChanges: { oldRoom: string; newRoom: string }[];
    ullivikDeparture: { count: number; details: string };
    dndRooms: { count: number; details: string };
    createdAt: string;
    createdBy: string;
}

export interface TimeOffRequest {
    id: string;
    userId: string;
    employeeId: string;
    staffName: string;
    startDate: string; // YYYY-MM-DD
    endDate: string; // YYYY-MM-DD
    reason: string;
    status: TimeOffRequestStatus;
    createdAt: string; // ISO String
    type: TimeOffRequestType;
    replacementEmployeeId: string | null;
}

export interface MemoCategory {
    id: string;
    name: string;
    color: string;
    status?: 'active' | 'inactive' | 'deleted';
    createdAt: string;
}

export interface MemoTopic {
    id: string;
    name: string;
    categoryId: string;
    status?: 'active' | 'inactive' | 'deleted';
    createdAt: string;
}

export interface MemoTag {
    id: string;
    name: string;
    status?: 'active' | 'inactive' | 'deleted';
    createdAt: string;
}

export interface Memo {
    id: string;
    title: string;
    content: string; // Rich text HTML
    categoryId: string;
    topicId: string;
    tags: { [tagId: string]: boolean };
    authorId: string;
    authorName: string;
    searchIndex: string; // Lowercase title + content for searching
    status?: 'active' | 'inactive' | 'deleted';
    createdAt: string;
    updatedAt: string;
}

export interface MemoIndex {
    [memoId: string]: boolean;
}

// --- TASK CONFIGURATION (TEMPLATES) ---
export interface TaskCategory {
    id: string;
    name: string;
    color: string;
    createdAt: string;
    updatedAt: string;
}

export interface TaskSubStepTemplate {
    name: string;
    order: number;
    required?: boolean;
    useDoubleFields?: boolean;
}

export interface TaskSubTaskTemplate {
    label: string;
    order: number;
    required?: boolean;
    subSteps?: TaskSubStepTemplate[];
}

export interface TaskTemplate {
    id: string;
    categoryId: string;
    title: string;
    description?: string;
    subTasks: TaskSubTaskTemplate[];
    active: boolean;
    createdAt: string;
    updatedAt: string;
}

// --- TASK MANAGER (INSTANCES) ---
export type MaintenanceStatus = 'Open' | 'In Progress' | 'On Hold' | 'Completed' | 'Cancelled';
export type MaintenancePriority = 'Low' | 'Medium' | 'High' | 'Urgent';
export type MaintenanceLocationType = 'Room' | 'Facility';

export interface MaintenanceHistory {
    action: string;
    status: MaintenanceStatus;
    changedBy: string;
    timestamp: string; // ISO String
    note?: string;
}

export interface MaintenanceRequest {
    id: string; // Database key
    requestId: string; // Display ID like MR-0001
    type: MaintenanceLocationType;
    roomNumber?: string;
    facility?: string;
    title: string;
    description: string;
    priority: MaintenancePriority;
    status: MaintenanceStatus;
    createdBy: string;
    createdAt: string; // ISO String
    updatedAt: string; // ISO String
    history: MaintenanceHistory[];
}

export interface TaskSubStepInstance {
    name: string;
    done: boolean;
    order: number;
    required?: boolean;
    useDoubleFields?: boolean;
    count?: string;
    details?: string;
}

export interface TaskSubTaskInstance {
    label: string;
    isDone: boolean;
    order: number;
    required?: boolean;
    subSteps?: TaskSubStepInstance[];
}

export interface TaskInstance {
    id: string;
    templateId: string;
    categoryId: string;
    title: string;
    description?: string;
    date: string; // YYYY-MM-DD
    status: 'pending' | 'completed' | 'skipped';
    subTasks: TaskSubTaskInstance[];
    completedAt?: string;
    completedBy?: string;
    notes?: string;
    createdAt: string;
    updatedAt: string;
}
